import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Ban, Trash2, ExternalLink } from "lucide-react";

const TABS = [
  { v: "false", label: "Pending" },
  { v: "true", label: "Approved" },
  { v: "", label: "All" },
];

export default function AdminCompanies() {
  const [params, setParams] = useSearchParams();
  const approved = params.get("approved") ?? "false";
  const [items, setItems] = useState([]);

  const load = () => {
    const s = approved === "" ? "" : `approved=${approved}`;
    api.get(`/admin/companies?${s}`).then(({ data }) => setItems(data.items || []));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [approved]);

  const doApprove = async (id) => { await api.post(`/admin/companies/${id}/approve`); toast.success("Approved"); load(); };
  const doUnapprove = async (id) => { await api.post(`/admin/companies/${id}/unapprove`); toast.success("Unapproved"); load(); };
  const doDelete = async (id) => {
    if (!window.confirm("Delete this company?")) return;
    await api.delete(`/admin/companies/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div>
      <div className="overline text-safety">Removal firms</div>
      <h1 className="mt-1 font-display font-black text-3xl tracking-tighter text-forest">Company approvals</h1>

      <div className="mt-6 flex border border-border w-fit">
        {TABS.map((t) => (
          <button
            key={t.label}
            onClick={() => setParams(t.v ? { approved: t.v } : {})}
            data-testid={`admin-companies-tab-${t.label.toLowerCase()}`}
            className={`px-5 h-10 text-xs overline transition-colors duration-200 ${approved === t.v ? "bg-forest text-white" : "bg-white hover:bg-sage"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6 border border-border bg-white">
        {items.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">Nothing here.</div>
        ) : items.map((c, i) => (
          <div key={c.company_id} data-testid={`admin-company-row-${c.company_id}`} className={`p-5 grid grid-cols-1 md:grid-cols-[1fr,auto] gap-4 items-start ${i > 0 ? "border-t border-border" : ""}`}>
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <Badge className={`rounded-none overline ${c.is_approved ? "bg-forest text-white" : "bg-safety text-white"}`}>{c.is_approved ? "approved" : "pending"}</Badge>
              </div>
              <div className="font-display text-lg tracking-tight">{c.name}</div>
              <div className="text-sm text-muted-foreground line-clamp-2 mt-1">{c.description}</div>
              <div className="text-xs text-muted-foreground mt-1">{c.email} · {c.phone} · areas: {c.service_areas?.slice(0,3).join(", ")}</div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button asChild variant="outline" size="sm" className="rounded-none">
                <Link to={`/removal-companies/${c.company_id}`} target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4 mr-1" /> View</Link>
              </Button>
              {!c.is_approved ? (
                <Button onClick={() => doApprove(c.company_id)} size="sm" data-testid={`admin-company-approve-${c.company_id}`} className="rounded-none bg-forest hover:bg-forest-800 text-white">
                  <Check className="w-4 h-4 mr-1" /> Approve
                </Button>
              ) : (
                <Button onClick={() => doUnapprove(c.company_id)} size="sm" variant="outline" data-testid={`admin-company-unapprove-${c.company_id}`} className="rounded-none border-safety text-safety hover:bg-safety hover:text-white">
                  <Ban className="w-4 h-4 mr-1" /> Unapprove
                </Button>
              )}
              <Button onClick={() => doDelete(c.company_id)} size="sm" variant="outline" data-testid={`admin-company-delete-${c.company_id}`} className="rounded-none border-destructive text-destructive hover:bg-destructive hover:text-white">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
